import React from "react";
import "../../styles/skill-match-modal.css";

/**
 * SkillMatchModal
 * Confirm-before-linking modal that reports how a task's required skills line
 * up against a job's skills before the link is committed to the RACI matrix.
 *
 * Props:
 * - open: boolean
 * - loading: boolean — API call in flight
 * - error: string | null — API failure message
 * - data: normalized /job-task-skills response (see jobTaskSkillService)
 * - jobName / taskName: labels for the header summary
 * - onConfirm(): commit the link
 * - onCancel(): dismiss without linking
 */
function SkillMatchModal({
  open,
  loading = false,
  error = null,
  data = null,
  jobName = "",
  taskName = "",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  const matched = data?.matched_skills || [];
  const missing = data?.missing_skills || [];
  const fully = !!data?.is_fully_matched;

  return (
    <div className="smm-backdrop" role="dialog" aria-modal="true">
      <div className="smm-panel">
        <div className="smm-header">
          <h3 className="smm-title">Skill Match</h3>
          <p className="smm-subtitle">
            Linking{" "}
            <strong>{taskName || "task"}</strong> with{" "}
            <strong>{jobName || "job"}</strong>
          </p>
        </div>

        <div className="smm-body">
          {loading ? (
            <div className="smm-state">Checking skills…</div>
          ) : error ? (
            <div className="smm-state smm-state-error">{error}</div>
          ) : (
            <>
              <div className="smm-counts">
                <span className="smm-count">
                  Required: <strong>{data?.required_count ?? 0}</strong>
                </span>
                <span className="smm-count smm-count-matched">
                  Matched: <strong>{data?.matched_count ?? matched.length}</strong>
                </span>
                <span className="smm-count smm-count-missing">
                  Missing: <strong>{data?.missing_count ?? missing.length}</strong>
                </span>
              </div>

              {fully && (
                <div className="smm-banner smm-banner-ok">
                  ✓ This job already has all the skills this task requires.
                </div>
              )}

              <div className="smm-section">
                <div className="smm-section-title smm-section-title-matched">
                  Matched skills ({matched.length})
                </div>
                {matched.length === 0 ? (
                  <div className="smm-empty">No matching skills.</div>
                ) : (
                  <ul className="smm-list">
                    {matched.map((s, i) => (
                      <li key={`m-${i}`} className="smm-item smm-item-matched">
                        <span className="smm-skill-name">{s.name}</span>
                        {s.matched_to && s.matched_to !== s.name && (
                          <span className="smm-matched-to">
                            matched to “{s.matched_to}”
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="smm-section">
                <div className="smm-section-title smm-section-title-missing">
                  Missing skills ({missing.length})
                </div>
                {missing.length === 0 ? (
                  <div className="smm-empty">None — nothing missing.</div>
                ) : (
                  <ul className="smm-list">
                    {missing.map((s, i) => (
                      <li key={`x-${i}`} className="smm-item smm-item-missing">
                        <span className="smm-skill-name">{s.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        <div className="smm-footer">
          <button
            type="button"
            className="smm-btn smm-btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="smm-btn smm-btn-primary"
            onClick={onConfirm}
            disabled={loading || !!error}
          >
            Confirm &amp; Link
          </button>
        </div>
      </div>
    </div>
  );
}

export default SkillMatchModal;
